import { useEffect, useRef, useState } from 'react';
import { Icon, ProtocolButton } from './ui';
import { FormatToolbar } from '@/components/message-editor/format-toolbar';
import { EmojiButton, LinkButton } from '@/components/message-editor/insert-actions';
import { useMessageEditor } from '@/components/message-editor/use-message-editor';
import { EMAIL_BODY_TEMPLATE, EMAIL_SUBJECT_TEMPLATE } from '@/modules/instructions/constants';
import type { EmailChipData } from '../utils';
import { rteIcons } from '../constants';
import { useEscalationPolicy } from '@/hooks/use-escalation-policy';
import { hasPolicy, missingEscalationFields } from '@/modules/escalation-policy/escalation-policy';
import { EscalationPolicyButton } from '@/modules/escalation-policy/escalation-policy-button';
import '@/components/message-editor/editor.css';

// Send Email composer.

/** Good enough for a composer: something@something.tld — the mail server is the real judge. */
const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const csv = (value: string) => (value ? value.split(',').map((email) => email.trim()).filter(Boolean) : []);

export function ProtocolEmailModal({
  existing,
  onSave,
  onClose,
  policyMode = false
}: {
  /** The chip's saved attrs when editing; null for a new chip, which starts on the templates. */
  existing: EmailChipData | null;
  onSave: (data: EmailChipData) => void;
  onClose: () => void;
  /** Editing the brand policy itself (Brand Settings): no policy to fall back to or show. */
  policyMode?: boolean;
}) {
  const { data: loaded } = useEscalationPolicy();
  const policy = policyMode ? undefined : loaded;
  // A brand with an email policy starts a new chip blank, so the policy fills it.
  const startBlank = !existing && hasPolicy(policy, 'send-email');
  const savedCc = csv(existing?.cc ?? '');
  const savedBcc = csv(existing?.bcc ?? '');

  // Comma-separated, same as candy's ReplyEmailHead — not chips.
  const [to, setTo] = useState<string[]>(csv(existing?.to ?? ''));
  const [cc, setCc] = useState<string[]>(savedCc);
  const [bcc, setBcc] = useState<string[]>(savedBcc);
  // A saved chip opens with the rows it actually uses already showing.
  const [ccOpen, setCcOpen] = useState(savedCc.length > 0);
  const [bccOpen, setBccOpen] = useState(savedBcc.length > 0);
  const [subject, setSubject] = useState(existing ? existing.subject : startBlank ? '' : EMAIL_SUBJECT_TEMPLATE);
  const [error, setError] = useState<string | null>(null);
  // What is typed into each address row but not yet a pill. Send takes it too: clicking Send
  // straight after typing a CC address must not drop it.
  const [drafts, setDrafts] = useState({ to: '', cc: '', bcc: '' });
  const first = useRef<HTMLInputElement>(null);

  // The Sentinel reply editor: the body is saved as the Markdown it shows, so what is composed
  // here is what the escalation email renders.
  const editor = useMessageEditor({
    initialMarkdown: existing ? existing.body : startBlank ? '' : EMAIL_BODY_TEMPLATE,
    editorKey: 'protocol-email',
    placeholder: hasPolicy(policy, 'send-email') ? 'Write your message, or leave it blank to use the brand policy...' : 'Write your message here...'
  });

  const withDraft = (emails: string[], draft: string) => [
    ...emails,
    ...csv(draft).filter((email) => !emails.includes(email))
  ];
  const allTo = withDraft(to, drafts.to).slice(0, 1);
  const allCc = withDraft(cc, drafts.cc);
  const allBcc = withDraft(bcc, drafts.bcc);
  const invalid = [...to, ...cc, ...bcc].filter((email) => !isEmail(email));
  // A refused Send's message stays until the addresses change.
  const changeTo = (next: string[]) => { setTo(next); setError(null); };
  const changeCc = (next: string[]) => { setCc(next); setError(null); };
  const changeBcc = (next: string[]) => { setBcc(next); setError(null); };
  const send = () => {
    const refused = [...allTo, ...allCc, ...allBcc].filter((email) => !isEmail(email));
    if (refused.length) return setError(`Not a valid email: ${refused.join(', ')}`);
    const data = {
      to: allTo.join(', '),
      cc: allCc.join(', '),
      bcc: allBcc.join(', '),
      subject: subject.trim(),
      body: editor.getMarkdown().trim()
    };
    // A blank field is fine when the brand's escalation policy sets it.
    const missing = policyMode ? [] : missingEscalationFields('send-email', { ...data }, policy);
    if (missing.length) return setError(`Add the ${missing.join(', ')} — the brand policy does not set it`);
    onSave(data);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape closes the top layer only; with a popover open that is Radix's job.
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    first.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/25'
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        role='dialog'
        aria-label='Send Email'
        // 473 = the design's 423 plus the second footer row and the field rules.
        className='flex h-[473px] max-h-[calc(100vh-32px)] w-188 max-w-[calc(100vw-32px)] flex-col overflow-clip rounded-2xl bg-[var(--glass-surface-light)] shadow-[0px_8px_32px_0px_rgba(0,0,0,0.12)] backdrop-blur-[20px]'
      >
        {/* Cancel at the foot closes the composer, so the header needs no X of its own. */}
        <div className='flex h-12 shrink-0 items-center justify-between px-3'>
          <p className='text-sm font-semibold leading-5 text-[var(--text-body)]'>{policyMode ? 'Send Email · brand policy' : 'Send Email'}</p>
          {policyMode ? null : <EscalationPolicyButton type='send-email' />}
        </div>
        <div className='h-px w-full shrink-0 bg-[var(--border-default)]' />

        <div className='flex min-h-px flex-1 flex-col px-4 pt-3'>
          <EmailField
            label='To'
            emails={to}
            onChange={changeTo}
            draft={drafts.to}
            onDraftChange={(draft) => setDrafts((current) => ({ ...current, to: draft }))}
            single
            placeholder={policy?.['send-email']?.['to'] ? `Brand policy: ${policy['send-email']['to']}` : 'Search people...'}
            firstRef={first}
            invalid={invalid}
            trailing={
              // ml-auto: To's input unmounts after one chip, so CC/BCC would slide left.
              <div className='ml-auto flex shrink-0 items-center gap-2 pl-3 text-[13px] leading-4.5 text-[var(--text-secondary)]'>
                {!ccOpen ? (
                  <button type='button' onClick={() => setCcOpen(true)} className='cursor-pointer hover:text-[var(--text-body)]'>
                    CC
                  </button>
                ) : null}
                {!bccOpen ? (
                  <button type='button' onClick={() => setBccOpen(true)} className='cursor-pointer hover:text-[var(--text-body)]'>
                    BCC
                  </button>
                ) : null}
              </div>
            }
          />
          <FieldRule />
          {ccOpen ? (
            <>
              <EmailField
                label='CC'
                emails={cc}
                onChange={changeCc}
                draft={drafts.cc}
                onDraftChange={(draft) => setDrafts((current) => ({ ...current, cc: draft }))}
                invalid={invalid}
                placeholder='Add an email'
              />
              <FieldRule />
            </>
          ) : null}
          {bccOpen ? (
            <>
              <EmailField
                label='BCC'
                emails={bcc}
                onChange={changeBcc}
                draft={drafts.bcc}
                onDraftChange={(draft) => setDrafts((current) => ({ ...current, bcc: draft }))}
                invalid={invalid}
                placeholder='Add an email'
              />
              <FieldRule />
            </>
          ) : null}

          <input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder={policy?.['send-email']?.['subject'] ? `Brand policy: ${policy['send-email']['subject']}` : 'Subject'}
            className='h-6 w-full shrink-0 bg-transparent text-[13px] leading-4.5 text-[var(--text-body)] outline-none placeholder:text-[var(--text-secondary)]'
          />
          <FieldRule />

          <div className='protocol-scroll min-h-px w-full flex-1 overflow-y-auto py-[3px]'>
            <div ref={editor.hostRef} className='message-editor protocol-email-editor h-full w-full' />
          </div>
        </div>

        <div className='flex shrink-0 flex-col gap-2 px-4 pb-[15px]'>
          {/* The toolbar spans the sheet; the actions sit on the row below it. */}
          <div className='flex h-10 w-full items-center gap-1 rounded-full bg-white/25 px-3'>
            <FormatToolbar editor={editor} />
            <EmojiButton editor={editor} />
            <LinkButton editor={editor} />
          </div>

          <div className='flex shrink-0 items-center justify-end gap-3'>
            {/* The design has no Send, but the composer needs a way to close on success. */}
            {error ? (
              <p role='alert' className='min-w-px flex-1 text-xs leading-4 text-[var(--text-error)]'>
                {error}
              </p>
            ) : null}
            <ProtocolButton variant='secondary' size='lg' onClick={onClose}>
              Cancel
            </ProtocolButton>
            <ProtocolButton variant='primary-dark' size='lg' onClick={send}>
              {policyMode ? 'Save' : 'Send'}
            </ProtocolButton>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The rule under each header row — subtle, and inset with the fields it separates. */
function FieldRule() {
  return <div className='my-2.5 h-px shrink-0 bg-[var(--border-subtle)]' />;
}

// To / CC / BCC; a committed address becomes a pill so it can be removed one at a time.
function EmailField({
  label,
  emails,
  onChange,
  placeholder,
  firstRef,
  trailing,
  autoFocus,
  single,
  invalid,
  draft,
  onDraftChange
}: {
  label: string;
  emails: string[];
  onChange: (next: string[]) => void;
  /** The address being typed, not yet a pill; held by the composer so Send can include it. */
  draft: string;
  onDraftChange: (draft: string) => void;
  placeholder: string;
  firstRef?: React.RefObject<HTMLInputElement | null>;
  trailing?: React.ReactNode;
  autoFocus?: boolean;
  /** To takes one recipient; CC and BCC take any number. */
  single?: boolean;
  /** Addresses Send refused — their pills are outlined in the error colour. */
  invalid?: string[];
}) {
  const setDraft = onDraftChange;

  const commit = (raw: string) => {
    const added = raw
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry && !emails.includes(entry));
    if (added.length) onChange(single ? added.slice(0, 1) : [...emails, ...added]);
    setDraft('');
  };

  return (
    <div className='flex min-h-6 shrink-0 flex-wrap items-center gap-y-1.5'>
      {/* One label width for all three rows, sized to "BCC"; at 24px it ran into its pills. */}
      <span className='w-8 shrink-0 text-[13px] leading-4.5 text-[var(--text-secondary)]'>{label}</span>
      {emails.map((email) => (
        <span
          key={email}
          className={`ml-[8px] flex h-[22px] shrink-0 items-center gap-[6px] rounded-[100px] border border-solid bg-[var(--surface-white)] pl-[10px] pr-[5px] text-[12px] leading-[16px] text-[var(--text-body)] ${
            invalid?.includes(email) ? 'border-[var(--text-error)]' : 'border-[var(--border-default)]'
          }`}
        >
          {email}
          <button
            type='button'
            title={`Remove ${email}`}
            onClick={() => onChange(emails.filter((entry) => entry !== email))}
            className='flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full text-[var(--text-secondary)] transition-colors hover:bg-[var(--neutral-200)]'
          >
            <Icon src={rteIcons.close} size={10} />
          </button>
        </span>
      ))}
      {single && emails.length ? null : (
        <input
          ref={firstRef}
          autoFocus={autoFocus}
          value={draft}
          onChange={(event) => (event.target.value.includes(',') ? commit(event.target.value) : setDraft(event.target.value))}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === 'Tab') {
              if (!draft.trim()) return;
              event.preventDefault();
              commit(draft);
              return;
            }
            // Backspace on an empty draft takes back the last pill, as every mail client does.
            if (event.key === 'Backspace' && !draft && emails.length) onChange(emails.slice(0, -1));
          }}
          onBlur={() => commit(draft)}
          placeholder={emails.length ? '' : placeholder}
          className='ml-2 min-w-20 flex-1 bg-transparent text-xs leading-[normal] text-[var(--text-body)] outline-none placeholder:text-[var(--grey-80)]'
        />
      )}
      {trailing}
    </div>
  );
}

